import { Button, EmptyState, Select, Spinner, Table, type TableColumn } from '@bennows/boows-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StatChart, type ChartSeries } from '../../../../web/src/components/charts/StatChart';
import { Figure, FigureGroup } from '../../components/Figures';
import { formatMoney, formatMonth, formatMonthShort, formatNumber, formatPercent } from '../../lib/format';
import { AI_USAGE_WINDOWS, useTenantAiUsage, type AiJobTypeMonth, type AiUsageMonth } from './useTenantUsage';

const CARD_BREAKPOINT = 640;
/** StatChart takes at most eight series; beyond that the smallest job types share one. */
const MAX_SERIES = 8;
const OTHER = 'other';

type Money = AiUsageMonth['cost'][number];

/** The job types of the window by credits, the smallest merged into "other" when there are too many. */
function seriesOf(months: readonly AiUsageMonth[]): { keys: string[]; merged: Set<string> } {
  const credits = new Map<string, number>();
  for (const month of months) for (const entry of month.by_job_type) credits.set(entry.job_type, (credits.get(entry.job_type) ?? 0) + entry.credits);
  const ranked = [...credits].filter(([, total]) => total > 0).sort((a, b) => b[1] - a[1]).map(([key]) => key);
  if (ranked.length <= MAX_SERIES) return { keys: ranked, merged: new Set() };
  return { keys: [...ranked.slice(0, MAX_SERIES - 1), OTHER], merged: new Set(ranked.slice(MAX_SERIES - 1)) };
}

/** AI runs, credits, tokens and real cost of one tenant by month and job type (REQ-140-013). */
export function TenantAiTab({ tenantId }: { readonly tenantId: string }) {
  const { t } = useTranslation();
  const [window, setWindow] = useState<number>(6);
  const [picked, setPicked] = useState<string | null>(null);
  const usage = useTenantAiUsage(tenantId, window);
  const job = (key: string) => t(`aiJobs.${key}`, { defaultValue: key });
  const money = (entries: readonly Money[]) => (entries.length === 0 ? t('tenantAi.noCost') : entries.map((entry) => formatMoney(entry)).join(' · '));

  const toolbar = (
    <div className="ui-console-toolbar">
      <Select
        hideLabel
        label={t('tenantAi.window')}
        value={String(window)}
        options={AI_USAGE_WINDOWS.map((months) => ({ id: String(months), label: t('tenantAi.windowOption', { months }) }))}
        onChange={(id) => setWindow(Number(id))}
      />
    </div>
  );

  if (usage.isPending) {
    return (
      <>
        {toolbar}
        <div className="ui-console-loading" role="status">
          <Spinner label={t('common.loading')} />
        </div>
      </>
    );
  }
  if (usage.isError) {
    return (
      <div className="ui-error-state" role="alert">
        <p className="ui-error-state__text">{t('tenantAi.loadError')}</p>
        <Button variant="secondary" onClick={() => void usage.refetch()}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  const months = usage.data.months;
  const total = usage.data.total;
  const { keys, merged } = seriesOf(months);
  const series: ChartSeries[] = keys.map((key) => ({ key, label: key === OTHER ? t('tenantAi.otherJobs') : job(key) }));
  const credit = (month: string, key: string) => {
    const entries = months.find((entry) => entry.month === month)?.by_job_type ?? [];
    return entries.filter((entry) => (key === OTHER ? merged.has(entry.job_type) : entry.job_type === key)).reduce((sum, entry) => sum + entry.credits, 0);
  };
  const latest = months.at(-1)!.month;
  const shown = months.find((month) => month.month === (picked ?? latest)) ?? months.at(-1)!;
  const rate = (entry: { runs: number; failed: number }) => (entry.runs === 0 ? t('tenantAi.noRuns') : formatPercent(entry.failed / entry.runs));
  const columns: TableColumn<AiJobTypeMonth>[] = [
    { key: 'job', header: t('tenantAi.columns.job'), cell: (row) => job(row.job_type) },
    { key: 'runs', header: t('tenantAi.columns.runs'), align: 'right', cell: (row) => formatNumber(row.runs) },
    { key: 'failed', header: t('tenantAi.columns.failed'), align: 'right', cell: (row) => `${formatNumber(row.failed)} (${rate(row)})` },
    { key: 'credits', header: t('tenantAi.columns.credits'), align: 'right', cell: (row) => formatNumber(row.credits) },
    { key: 'tokens', header: t('tenantAi.columns.tokens'), align: 'right', cell: (row) => t('tenantAi.tokens', { input: formatNumber(row.input_tokens), output: formatNumber(row.output_tokens) }) },
    { key: 'cost', header: t('tenantAi.columns.cost'), align: 'right', cell: (row) => money(row.cost) },
  ];

  return (
    <>
      {toolbar}
      <FigureGroup title={t('tenantAi.totals', { months: window })}>
        <Figure label={t('tenantAi.runs')} value={formatNumber(total.runs)} />
        <Figure label={t('tenantAi.failureRate')} value={rate(total)} />
        <Figure label={t('tenantAi.credits')} value={formatNumber(total.credits)} />
        <Figure label={t('tenantAi.inputTokens')} value={formatNumber(total.input_tokens)} />
        <Figure label={t('tenantAi.outputTokens')} value={formatNumber(total.output_tokens)} />
        <Figure text label={t('tenantAi.cost')} value={money(total.cost)} />
      </FigureGroup>
      {series.length > 0 ? (
        <StatChart
          title={t('tenantAi.chartTitle')}
          kind="stacked"
          categories={months.map((month) => ({ key: month.month, label: formatMonthShort(month.month) }))}
          series={series}
          value={credit}
          tableCaption={t('tenantAi.chartCaption')}
        />
      ) : null}
      <section className="ui-card ui-stack" aria-label={t('tenantAi.byJob', { month: formatMonth(shown.month) })}>
        <div className="ui-console-figures__head">
          <h2 className="ui-console-figures__title">{t('tenantAi.byJobTitle')}</h2>
          <div className="ui-console-toolbar">
            <Select
              hideLabel
              label={t('tenantAi.month')}
              value={shown.month}
              options={[...months].reverse().map((month) => ({ id: month.month, label: formatMonth(month.month) }))}
              onChange={setPicked}
            />
          </div>
        </div>
        <Table
          ariaLabel={t('tenantAi.byJobLabel')}
          rows={shown.by_job_type}
          columns={columns}
          getRowId={(row) => row.job_type}
          emptyState={<EmptyState title={t('tenantAi.empty')} />}
          labels={{ empty: t('tenantAi.empty') }}
          cardBreakpoint={CARD_BREAKPOINT}
          viewToggle={false}
          mobileViewToggle={false}
          renderCard={(row) => (
            <div className="ui-console-card">
              <strong>{job(row.job_type)}</strong>
              <span>{t('tenantAi.card', { runs: row.runs, credits: row.credits })}</span>
              <span className="ui-console-muted">
                {t('tenantAi.cardFailed', { failed: row.failed, rate: rate(row) })} · {money(row.cost)}
              </span>
            </div>
          )}
        />
      </section>
    </>
  );
}
